import type { JSX, VoidComponent } from "solid-js";
import { createMemo } from "solid-js";
import type { ExtendableInputProps } from "./Input";
import Input from "./Input";
import { IGNORE_PASSWORD_MANAGER_PROPS } from "../../constants";

type TextProps = {
  id: string;
  ariaLabel?: string;
  value?: string;
  disabled?: boolean;
  placeholder?: string;
  errorMessage?: string;
  onBlur: JSX.FocusEventHandler<HTMLInputElement, FocusEvent>;
  onInput?: JSX.InputEventHandler<HTMLInputElement, InputEvent>;
  autoComplete?: boolean;
};
type TextInputProps = TextProps & ExtendableInputProps;

const BaseTextInput: VoidComponent<TextProps> = (props) => {
  const hasError = createMemo(() => props.errorMessage !== undefined);
  const isDisabled = createMemo(() => props.disabled === true);
  const classes = createMemo(() => {
    if (hasError()) {
      return "bg-surface-destructive border-error";
    } else if (isDisabled()) {
      return "bg-surface-disabled border-disabled";
    } else {
      return "bg-surface-default border-default";
    }
  });

  return (
    <input
      id={props.id}
      aria-label={props.ariaLabel}
      type="text"
      class="w-full truncate rounded-sm border p-2 text-base-regular text-primary placeholder:text-base-regular focus-visible:outline-default"
      classList={{ [classes()]: true }}
      value={props.value ?? ""}
      disabled={isDisabled()}
      placeholder={props.placeholder}
      onInput={(e) => {
        props.onInput?.(e);
      }}
      onBlur={(e) => {
        props.onBlur(e);
      }}
      {...(props.autoComplete !== true && { ...IGNORE_PASSWORD_MANAGER_PROPS })}
    />
  );
};

export const TextInput: VoidComponent<Omit<TextInputProps, "id">> = (props) => (
  <Input {...props}>{(id) => <BaseTextInput {...props} id={id} />}</Input>
);

export const CompactTextInput: VoidComponent<TextProps> = (props) => <BaseTextInput {...props} />;
